import type { LogLevel, OpenOptions, QuarterzipConfig } from './types';

/** The dedicated SDK origin the dynamic iframe documents are served from in production. */
const DEFAULT_FRAME_ORIGIN = 'https://sdk.quarterzip.ai';
const DEFAULT_LOG_LEVEL: LogLevel = 'warn';

let globalConfig: QuarterzipConfig = {};
export const setConfig = (config: QuarterzipConfig): void => {
	globalConfig = { ...globalConfig, ...config };
};

let activeLogLevel: LogLevel = DEFAULT_LOG_LEVEL;
export const logLevel = (): LogLevel => activeLogLevel;

export interface ResolvedFrames {
	callUrl: string;
	pipUrl: string;
	origin: string;
}

/**
 * Resolve the frame origin and log level (per-call override > global config > built-in default) into
 * concrete frame URLs and the shared origin. Throws if the resolved origin is not a valid absolute
 * URL.
 */
export const resolveFrames = (agentId: string, options: OpenOptions = {}): ResolvedFrames => {
	const configured = options.frameOrigin ?? globalConfig.frameOrigin ?? DEFAULT_FRAME_ORIGIN;

	let url: URL;
	try {
		url = new URL(configured);
	} catch {
		throw new Error(`[Quarterzip] Invalid frameOrigin: ${configured}`);
	}

	const hostOrigin = typeof location !== 'undefined' ? location.origin : '';

	if (url.origin === hostOrigin && !globalConfig.allowSameOrigin) {
		// Same-origin framing defeats the sandbox: `allow-scripts` + `allow-same-origin` on a
		// same-origin document lets the framed code remove its own sandbox and reach the host DOM.
		// eslint-disable-next-line no-restricted-properties
		console.warn(
			'[Quarterzip] frameOrigin is on your own origin. This disables the iframe sandbox isolation. ' +
				'Serve the SDK from a distinct origin.'
		);
	}

	activeLogLevel = options.logLevel ?? globalConfig.logLevel ?? DEFAULT_LOG_LEVEL;

	const deploymentPath = `${url.origin}/sdk/d/${encodeURIComponent(agentId)}`;
	// The frames validate every inbound control message against `host` and use it as the target
	// origin for everything they post back. `referrerpolicy=no-referrer` on the iframes means this
	// param is their only way to learn it.
	const query = new URLSearchParams({ host: hostOrigin, log: activeLogLevel });
	return {
		callUrl: `${deploymentPath}/call?${query}`,
		pipUrl: `${deploymentPath}/pip?${query}`,
		origin: url.origin
	};
};
